import { Percentage } from '@sparkdotfi/common-universal'
import { UseFormReturn } from 'react-hook-form'

import { FormFieldsForAssetClass } from '../logic/form/form'
import { EasyBorrowFormSchema } from '../logic/form/validation'
import { ExistingPosition, PageStatus } from '../logic/types'
import { BorrowDetails } from '../logic/useEasyBorrow'
import { EasyBorrowForm } from './form/EasyBorrowForm'
import { EasyBorrowSidePanel } from './side-panel/EasyBorrowSidePanel'
import { tokenLogos } from '@/config/chain/configs/mainnet/assets'
import { ActionsViewState } from '@/features/actions/types'
import { ActionsView } from '@/features/actions/views/ActionsView'
import { LiquidationDetails } from '@/features/sparklend/common/data/market-info/getLiquidationDetails'
import { UserPositionSummary } from '@/features/sparklend/common/data/market-info/marketInfo'
import { RiskAcknowledgementInfo } from '@/features/sparklend/common/utils/liquidation-risk-warning/types'
import { Button } from '@/ui/atoms/button/Button'
import { Form } from '@/ui/atoms/form/Form'
import { Panel } from '@/ui/atoms/panel/Panel'
import { ConnectOrSandboxCTAPanel } from '@/ui/organisms/connect-or-sandbox-cta-panel/ConnectOrSandboxCTAPanel'
import { RiskAcknowledgement } from '@/ui/organisms/risk-acknowledgement/RiskAcknowledgement'

export interface EasyBorrowPanelProps {
  pageStatus: PageStatus
  form: UseFormReturn<EasyBorrowFormSchema>
  assetsToBorrowFields: FormFieldsForAssetClass
  assetsToDepositFields: FormFieldsForAssetClass
  alreadyDeposited: ExistingPosition
  alreadyBorrowed: ExistingPosition
  updatedPositionSummary: UserPositionSummary
  setDesiredLoanToValue: (desiredLtv: Percentage) => void
  liquidationDetails?: LiquidationDetails
  riskAcknowledgement: RiskAcknowledgementInfo
  borrowDetails: BorrowDetails
  guestMode: boolean
  openConnectModal: () => void
  openSandboxModal: () => void
  focusOnActionsPanel: (node: HTMLDivElement | null) => void
  actionsViewState: ActionsViewState
}

const TOKEN_ICONS = [tokenLogos.usds, tokenLogos.eth, tokenLogos.dai, tokenLogos.wsteth, tokenLogos.usdc]

export function EasyBorrowPanel(props: EasyBorrowPanelProps) {
  const { pageStatus, actionsViewState, form, guestMode, openConnectModal, openSandboxModal } = props

  const disabled = pageStatus.state !== 'form'

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(pageStatus.submitForm)} className="flex flex-1 flex-col gap-4">
        <Panel className="flex flex-col gap-3 bg-tertiary p-1.5" spacing="none">
          <EasyBorrowForm {...props} disabled={disabled} />

          <div className="xl:hidden">
            <EasyBorrowSidePanel
              {...props.borrowDetails}
              hf={props.pageStatus.state === 'confirmation' ? props.updatedPositionSummary.healthFactor : undefined}
              liquidationDetails={props.liquidationDetails}
            />
          </div>

          {pageStatus.state === 'confirmation' && (
            <div className="flex flex-col gap-3">
              {!!props.riskAcknowledgement.warning && (
                <Panel className="flex flex-col gap-6">
                  {props.riskAcknowledgement.warning && (
                    <RiskAcknowledgement
                      onStatusChange={props.riskAcknowledgement.onStatusChange}
                      warning={props.riskAcknowledgement.warning}
                    />
                  )}
                </Panel>
              )}
              <Panel ref={props.focusOnActionsPanel}>
                <ActionsView
                  state={actionsViewState}
                  actionsGridLayout="extended"
                  allowChangeWhenActionsInProgress={false}
                />
              </Panel>
            </div>
          )}
        </Panel>

        {guestMode ? (
          <ConnectOrSandboxCTAPanel
            header="Connect your wallet to use Spark"
            iconPaths={TOKEN_ICONS}
            action={openConnectModal}
            buttonText="Connect wallet"
            openSandboxModal={openSandboxModal}
          />
        ) : (
          !disabled && (
            <Button
              type="submit"
              size="m"
              variant="primary"
              disabled={!form.formState.isValid}
              className="disabled:before:bg-tertiary"
            >
              Borrow
            </Button>
          )
        )}
      </form>
    </Form>
  )
}
